import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { ScreenplayEditor } from '@/components/editor/ScreenplayEditor';
import { SceneOutline } from '@/components/editor/SceneOutline';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { api } from '@/utils/api';

interface Script {
    id: string;
    title: string;
    description?: string;
    content: string;
}

export function Editor() {
    const { scriptId } = useParams<{ scriptId: string }>();
    const [script, setScript] = useState<Script | null>(null);
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [title, setTitle] = useState('');
    const [loading, setLoading] = useState(true);
    const [editorContent, setEditorContent] = useState(''); // Track editor changes

    useEffect(() => {
        loadScript();
    }, [scriptId]);

    const loadScript = async () => {
        if (!scriptId) return;

        try {
            const data = await api.getScript(scriptId);
            setScript(data);
            setTitle(data.title);
            setEditorContent(data.content);
        } catch (error) {
            console.error('Failed to load script:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveTitle = async () => {
        if (!scriptId || !title) return;

        try {
            await api.updateScript(scriptId, { title });
            setIsEditingTitle(false);
            await loadScript();
        } catch (error) {
            console.error('Failed to update title:', error);
        }
    };

    const handleExport = async () => {
        if (!scriptId) return;
        console.log('Exporting script:', scriptId, script?.title);

        try {
            // Fetch the response directly to access headers
            const response = await fetch(`/api/scripts/${scriptId}/export`, {
                credentials: 'include',
            });

            if (!response.ok) {
                throw new Error(`Export failed: ${response.statusText}`);
            }

            // Get filename from Content-Disposition header or use script title
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = `${script?.title.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'script'}.fountain`;

            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            console.log('Export filename from header:', filename);

            const blob = await response.blob();
            console.log('Export blob received:', blob.size, 'bytes');

            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();

            // Cleanup
            setTimeout(() => {
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }, 100);
        } catch (error) {
            console.error('Failed to export script:', error);
            alert('Export failed: ' + (error as Error).message);
        }
    };

    const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        console.log('Importing file:', file.name, file.size, 'bytes');

        if (!file.name.endsWith('.fountain')) {
            alert('Please select a .fountain file');
            e.target.value = ''; // Reset input
            return;
        }

        try {
            const text = await file.text();
            console.log('Import file content length:', text.length);
            if (scriptId) {
                await api.updateScript(scriptId, { content: text });
                console.log('Script updated, reloading...');
                await loadScript();
                alert('Script imported successfully!');
            }
        } catch (error) {
            console.error('Failed to import script:', error);
            alert('Import failed: ' + (error as Error).message);
        } finally {
            e.target.value = ''; // Reset input for next import
        }
    };

    const handleExportPDF = async () => {
        if (!script) return;

        try {
            // Dynamic imports for libraries  
            const fountainModule = await import('fountain-js');
            const { jsPDF } = await import('jspdf');

            // fountain-js exports a Fountain class, not a parse function
            const fountain = new fountainModule.Fountain();
            const parsed = fountain.parse(editorContent || script.content);

            // Create PDF with proper screenplay formatting
            const doc = new jsPDF({
                unit: 'in',
                format: 'letter',
            });

            // Screenplay standard margins (in inches)
            const margins = {
                top: 1.0,
                bottom: 1.0,
                left: 1.5,
                right: 1.0,
            };

            const pageWidth = 8.5;
            const pageHeight = 11.0;
            const contentWidth = pageWidth - margins.left - margins.right;

            let yPos = margins.top;
            const lineHeight = 0.167; // 12pt line spacing
            const fontSize = 12;

            doc.setFont('courier');
            doc.setFontSize(fontSize);

            // Function to add new page if needed
            const checkPageBreak = () => {
                if (yPos > pageHeight - margins.bottom) {
                    doc.addPage();
                    yPos = margins.top;
                }
            };

            // Render each element with proper formatting
            for (const token of parsed.tokens) {
                checkPageBreak();

                switch (token.type) {
                    case 'scene_heading':
                        if (!token.text) break;
                        yPos += lineHeight; // Space before scene heading
                        doc.setFont('courier', 'bold');
                        doc.text(token.text.toUpperCase(), margins.left, yPos);
                        doc.setFont('courier', 'normal');
                        yPos += lineHeight;
                        break;

                    case 'action':
                        if (!token.text) break;
                        const actionLines = doc.splitTextToSize(token.text, contentWidth);
                        actionLines.forEach((line: string) => {
                            checkPageBreak();
                            doc.text(line, margins.left, yPos);
                            yPos += lineHeight;
                        });
                        yPos += lineHeight * 0.5; // Half-line after action
                        break;

                    case 'character':
                        if (!token.text) break;
                        yPos += lineHeight; // Space before character
                        // Character names centered at 3.7" from left
                        doc.text(token.text.toUpperCase(), 3.7, yPos);
                        yPos += lineHeight;
                        break;

                    case 'parenthetical':
                        if (!token.text) break;
                        // Parenthetical indented to 3.1" from left
                        doc.text(token.text, 3.1, yPos);
                        yPos += lineHeight;
                        break;

                    case 'dialogue':
                        if (!token.text) break;
                        // Dialogue at 2.5" from left, max width 3.5"
                        const dialogueLines = doc.splitTextToSize(token.text, 3.5);
                        dialogueLines.forEach((line: string) => {
                            checkPageBreak();
                            doc.text(line, 2.5, yPos);
                            yPos += lineHeight;
                        });
                        break;

                    case 'transition':
                        if (!token.text) break;
                        yPos += lineHeight;
                        // Transitions right-aligned
                        const transWidth = doc.getTextWidth(token.text.toUpperCase());
                        doc.text(token.text.toUpperCase(), pageWidth - margins.right - transWidth, yPos);
                        yPos += lineHeight * 1.5;
                        break;

                    default:
                        // Handle other types as action
                        if (token.text) {
                            const lines = doc.splitTextToSize(token.text, contentWidth);
                            lines.forEach((line: string) => {
                                checkPageBreak();
                                doc.text(line, margins.left, yPos);
                                yPos += lineHeight;
                            });
                        }
                }
            }

            // Save the PDF
            doc.save(`${script.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`);

        } catch (error) {
            console.error('Failed to export PDF:', error);
            alert('PDF export failed: ' + (error as Error).message);
        }
    };

    const handleSceneClick = (lineNumber: number) => {
        // TODO: Scroll to line in editor
        console.log('Navigate to line:', lineNumber);
    };

    // Handler for editor content changes
    const handleContentChange = (newContent: string) => {
        setEditorContent(newContent);
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
                <Header />
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Loading...
                </div>
            </div>
        );
    }

    if (!script) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
                <Header />
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Script not found
                </div>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
            <Header />

            {/* Editor Toolbar */}
            <div
                style={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    borderBottom: '1px solid var(--color-border)',
                    padding: 'var(--spacing-md) var(--spacing-xl)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <div className="flex items-center gap-md">
                    {isEditingTitle ? (
                        <div className="flex items-center gap-sm">
                            <Input
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                style={{ width: '300px' }}
                                autoFocus
                            />
                            <Button variant="primary" onClick={handleSaveTitle}>
                                Save
                            </Button>
                            <Button variant="ghost" onClick={() => {
                                setIsEditingTitle(false);
                                setTitle(script.title);
                            }}>
                                Cancel
                            </Button>
                        </div>
                    ) : (
                        <h2
                            style={{ margin: 0, cursor: 'pointer' }}
                            onClick={() => setIsEditingTitle(true)}
                        >
                            {script.title}
                        </h2>
                    )}
                </div>

                <div className="flex items-center gap-md">
                    {/* Operations Dropdown */}
                    <DropdownMenu.Root>
                        <DropdownMenu.Trigger
                            className="btn btn-primary"
                            style={{
                                cursor: 'pointer',
                            }}
                        >
                            Operations ▾
                        </DropdownMenu.Trigger>

                        <DropdownMenu.Content
                            align="end"
                            sideOffset={8}
                            style={{
                                backgroundColor: 'var(--color-bg-elevated)',
                                border: '1px solid var(--color-border)',
                                borderRadius: 'var(--radius-md)',
                                padding: 'var(--spacing-sm)',
                                boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
                                minWidth: '200px',
                                zIndex: 99999,
                            }}
                        >
                            <DropdownMenu.Item
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    cursor: 'pointer',
                                    borderRadius: 'var(--radius-sm)',
                                    outline: 'none',
                                    color: 'var(--color-text-primary)',
                                }}
                                onSelect={() => document.getElementById('fountain-import')?.click()}
                            >
                                Import .fountain
                            </DropdownMenu.Item>

                            <DropdownMenu.Item
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    cursor: 'pointer',
                                    borderRadius: 'var(--radius-sm)',
                                    outline: 'none',
                                    color: 'var(--color-text-primary)',
                                }}
                                onSelect={handleExport}
                            >
                                Export .fountain
                            </DropdownMenu.Item>

                            <DropdownMenu.Separator
                                style={{
                                    height: '1px',
                                    backgroundColor: 'var(--color-border)',
                                    margin: 'var(--spacing-xs) 0',
                                }}
                            />

                            <DropdownMenu.Item
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    cursor: 'pointer',
                                    borderRadius: 'var(--radius-sm)',
                                    outline: 'none',
                                    color: 'var(--color-text-primary)',
                                }}
                                onSelect={handleExportPDF}
                            >
                                Export PDF
                            </DropdownMenu.Item>
                        </DropdownMenu.Content>
                    </DropdownMenu.Root>

                    <input
                        type="file"
                        accept=".fountain"
                        onChange={handleImport}
                        style={{ display: 'none' }}
                        id="fountain-import"
                    />
                </div>
            </div>

            {/* Main Editor Area with Sidebar */}
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                <SceneOutline content={editorContent} onSceneClick={handleSceneClick} />
                <div style={{ flex: 1, overflow: 'hidden' }}>
                    <ScreenplayEditor
                        scriptId={scriptId!}
                        initialContent={script.content}
                        onContentChange={handleContentChange}
                    />
                </div>
            </div>
        </div>
    );
}
